import { ArrowDown, ArrowUp, ChevronDown, Copy, ListChecks, Plus, Trash2, Type, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ColorField } from './ColorField';
// Image upload is switched off for now; uncomment this and the <ImageField> below to bring it back.
// It also needs a migration creating the public `coach-guide-assets` storage bucket (admin-only upload).
// import { ImageField } from './ImageField';
import { RichTextEditor } from './RichTextEditor';
import {
  GUIDE_LIMITS, emptyPrompt, richPlain, type GuidePrompt, type GuideSection, type GuideTypography,
} from '@/lib/coach-guide';

const FieldError = ({ msg }: { msg?: string }) => (msg ? <p className="text-xs text-destructive">{msg}</p> : null);

function IconButton({ label, onClick, disabled, children, destructive }: {
  label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode; destructive?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`h-8 w-8 ${destructive ? 'text-muted-foreground hover:text-destructive' : 'text-muted-foreground'}`}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function SectionEditor({
  section,
  index,
  total,
  expanded,
  active,
  errors,
  themeAccent,
  typography,
  onToggle,
  onChange,
  onMove,
  onDuplicate,
  onDelete,
  onFocus,
}: {
  section: GuideSection;
  index: number;
  total: number;
  expanded: boolean;
  /** The section being edited: outlined in its accent colour so its edges are easy to see. */
  active: boolean;
  /** Only this section's errors, keyed as in validateGuide(). */
  errors: Record<string, string>;
  themeAccent: string;
  /** Default sizes from the Style tab, shown in each editor's size menu. */
  typography: GuideTypography;
  onToggle: () => void;
  onChange: (next: GuideSection) => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onFocus: () => void;
}) {
  const s = section;
  const p = `sections.${s.id}`;
  const errorCount = Object.keys(errors).length;
  const set = (patch: Partial<GuideSection>) => onChange({ ...s, ...patch });
  const setItem = (id: string, patch: Partial<GuidePrompt>) =>
    set({ items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) });
  const isPrompts = s.kind === 'prompts';

  return (
    <Card
      className={`overflow-hidden transition-[border-color,box-shadow] duration-150 ${errorCount && !active ? 'border-destructive/60' : ''}`}
      style={active ? { borderColor: s.accent || themeAccent, boxShadow: `0 0 0 1px ${s.accent || themeAccent}` } : undefined}
      // Pointer as well as focus: Safari doesn't focus buttons on click.
      onFocusCapture={onFocus}
      onPointerDownCapture={onFocus}
    >
      {/* Header: always visible, click to expand. */}
      <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={expanded}
        >
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white"
            style={{ background: s.accent || themeAccent }}
          >
            {isPrompts ? (
              <ListChecks className="h-4 w-4" />
            ) : (
              <Type className="h-4 w-4" />
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">
              {richPlain(s.eyebrow).trim() || <span className="italic text-muted-foreground">No label</span>}
            </span>
            <span className="block text-xs text-muted-foreground">
              Section {index + 1} · {isPrompts ? `${s.items.length} prompt${s.items.length === 1 ? '' : 's'}` : 'Text block'}
            </span>
          </span>
          {errorCount > 0 && (
            <Badge variant="destructive" className="shrink-0 gap-1">
              <AlertCircle className="h-3 w-3" /> {errorCount}
            </Badge>
          )}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <div className="flex shrink-0 items-center border-l pl-1">
          <IconButton label="Move up" onClick={() => onMove(-1)} disabled={index === 0}><ArrowUp className="h-4 w-4" /></IconButton>
          <IconButton label="Move down" onClick={() => onMove(1)} disabled={index === total - 1}><ArrowDown className="h-4 w-4" /></IconButton>
          <IconButton label="Duplicate section" onClick={onDuplicate}><Copy className="h-4 w-4" /></IconButton>
          <IconButton label="Delete section" onClick={onDelete} destructive><Trash2 className="h-4 w-4" /></IconButton>
        </div>
      </div>

      {expanded && (
        <div className="space-y-5 border-t bg-muted/20 px-3 py-4 sm:px-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Section label <span className="font-normal text-muted-foreground">(optional)</span></label>
            <RichTextEditor
              singleLine
              compact
              minRows={1}
              value={s.eyebrow}
              onChange={(eyebrow) => set({ eyebrow })}
              baseSize={typography.eyebrowSize}
              max={GUIDE_LIMITS.eyebrow}
              placeholder="e.g. START HERE"
              invalid={!!errors[`${p}.eyebrow`]}
              ariaLabel="Section label"
            />
            <FieldError msg={errors[`${p}.eyebrow`]} />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {isPrompts ? 'Intro text' : 'Text'}{' '}
              {(isPrompts || !!s.image) && <span className="font-normal text-muted-foreground">(optional)</span>}
            </label>
            <RichTextEditor
              value={s.body}
              onChange={(body) => set({ body })}
              minRows={isPrompts ? 2 : 4}
              baseSize={typography.bodySize}
              max={GUIDE_LIMITS.body}
              placeholder={isPrompts ? 'A short line shown above the prompts' : 'What creators should know'}
              invalid={!!errors[`${p}.body`]}
              ariaLabel={isPrompts ? 'Intro text' : 'Text'}
            />
            <FieldError msg={errors[`${p}.body`]} />
          </div>

          {/* Image upload switched off for now.
          <ImageField value={s.image} error={errors[`${p}.image`]} onChange={(image) => set({ image })} />
          */}

          <div className="space-y-3 rounded-lg border bg-background p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Custom accent colour</p>
                <p className="text-xs text-muted-foreground">Off: uses the guide’s accent from the Style tab.</p>
              </div>
              <Switch
                checked={s.accent != null}
                onCheckedChange={(on) => set({ accent: on ? themeAccent : null })}
                aria-label="Custom accent colour for this section"
              />
            </div>
            {s.accent != null && (
              <ColorField label="Section accent" value={s.accent} error={errors[`${p}.accent`]} onChange={(v) => set({ accent: v })} />
            )}
          </div>

          {isPrompts && (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium">Prompts</p>
                <p className="text-xs text-muted-foreground">Copy gives creators the plain text (links include their address). Use [brackets] for parts they fill in.</p>
              </div>
              <FieldError msg={errors[`${p}.items`]} />

              {s.items.map((it, i) => {
                const titleErr = errors[`items.${it.id}.title`];
                const textErr = errors[`items.${it.id}.text`];
                return (
                  <div key={it.id} className={`space-y-2 rounded-lg border bg-background p-3 ${titleErr || textErr ? 'border-destructive/60' : ''}`}>
                    <div className="flex items-start gap-2">
                      <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <RichTextEditor
                          singleLine
                          compact
                          minRows={1}
                          value={it.title}
                          onChange={(title) => setItem(it.id, { title })}
                          baseSize={typography.promptTitleSize}
                          max={GUIDE_LIMITS.promptTitle}
                          placeholder="Prompt title, e.g. Find products that fit me"
                          invalid={!!titleErr}
                          ariaLabel={`Prompt ${i + 1} title`}
                        />
                      </div>
                      <div className="flex shrink-0 items-center">
                        <IconButton label="Move prompt up" onClick={() => set({ items: move(s.items, i, i - 1) })} disabled={i === 0}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </IconButton>
                        <IconButton label="Move prompt down" onClick={() => set({ items: move(s.items, i, i + 1) })} disabled={i === s.items.length - 1}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </IconButton>
                        <IconButton label="Delete prompt" onClick={() => set({ items: s.items.filter((x) => x.id !== it.id) })} destructive>
                          <Trash2 className="h-3.5 w-3.5" />
                        </IconButton>
                      </div>
                    </div>
                    <FieldError msg={titleErr} />
                    <RichTextEditor
                      compact
                      minRows={3}
                      value={it.text}
                      onChange={(text) => setItem(it.id, { text })}
                      baseSize={typography.promptTextSize}
                      max={GUIDE_LIMITS.promptText}
                      placeholder="The prompt creators copy into Claude"
                      invalid={!!textErr}
                      ariaLabel={`Prompt ${i + 1} text`}
                    />
                    <FieldError msg={textErr} />
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed"
                disabled={s.items.length >= GUIDE_LIMITS.items}
                onClick={() => set({ items: [...s.items, emptyPrompt()] })}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {s.items.length >= GUIDE_LIMITS.items ? `Limit of ${GUIDE_LIMITS.items} prompts reached` : 'Add prompt'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
